import type { User } from '../lib/auth';

/** Unwrap list payloads from API (array or { data } / { items }). */
export function unwrapArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object') {
    const payload = data as { data?: unknown; items?: unknown };
    if (Array.isArray(payload.data)) {
      return payload.data as T[];
    }
    if (Array.isArray(payload.items)) {
      return payload.items as T[];
    }
  }
  return [];
}

export interface Organization {
  id: string;
  name?: string;
  onboardingCompletedAt?: string | null;
  [key: string]: unknown;
}

export interface PlatformCredentials {
  platform?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: string;
  apiKey?: string;
  [key: string]: unknown;
}

export interface ConnectedPlatform {
  platform: string;
  displayName?: string;
  platformName?: string;
  connectedAt?: string;
  lastSyncAt?: string;
  isActive?: boolean;
  comingSoon?: boolean;
  credentials?: PlatformCredentials;
  syncFrequency?: number;
  [key: string]: unknown;
}

export interface PlatformInfo {
  platform: string;
  platformName?: string;
  displayName?: string;
  comingSoon?: boolean;
  [key: string]: unknown;
}

export interface GoogleAccountWithLocations {
  accountId?: string;
  accountName?: string;
  type?: string;
  locations?: GoogleLocation[];
  [key: string]: unknown;
}

export interface GoogleLocation {
  locationId: string;
  locationName?: string;
  accountId?: string;
  title?: string;
  address?: string;
  name?: string;
  storeCode?: string;
  [key: string]: unknown;
}

export interface ConnectPlatformResponse extends GoogleAccountsResponse {
  accountsAndLocations?: Array<{ locations?: GoogleLocation[] }>;
}

export interface GoogleAccountsResponse {
  locations?: GoogleLocation[];
  accountsAndLocations?: Array<{ locations?: GoogleLocation[] }>;
  url?: string;
}

export interface Business {
  id: string;
  name?: string;
  organizationId?: string;
  description?: string;
  businessType?: string;
  location?: Record<string, unknown>;
  phoneNumber?: string;
  website?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  connectedPlatforms?: ConnectedPlatform[];
  reviewCount?: number;
  averageRating?: number;
  [key: string]: unknown;
}

export interface BusinessInput {
  name?: string;
  description?: string;
  website?: string;
  phoneNumber?: string;
  email?: string;
  businessType?: string;
  location?: Record<string, unknown>;
  isActive?: boolean;
}

export interface PlatformSettingsInput {
  syncFrequency?: number;
  accessToken?: string;
  refreshToken?: string;
}

export interface ReviewAnalysisMetadata {
  provider?: string;
  modelUsed?: string;
  model_used?: string;
  processingTime?: number;
  processing_time?: number;
  languageDetected?: string;
  language_detected?: string;
  tokensUsed?: number;
  tokens_used?: number;
  cached?: boolean;
  [key: string]: unknown;
}

export interface ReviewAnalysis {
  summary?: string;
  sentiment?: string;
  suggestions?: Array<string | { text?: string }>;
  topics?: unknown[];
  keywords?: unknown[];
  analysisMetadata?: ReviewAnalysisMetadata;
  [key: string]: unknown;
}

export interface ReviewRawData {
  data?: string;
  parsedData?: unknown;
  [key: string]: unknown;
}

export interface ApiKeyRecord {
  id: string;
  label: string;
  scopes?: string[];
  isActive?: boolean;
  createdAt?: string;
  lastUsedAt?: string | null;
  [key: string]: unknown;
}

export interface ApiKeyCreateResponse {
  id: string;
  rawKey: string;
  label: string;
  createdAt: string;
  [key: string]: unknown;
}

export interface AgencyClient {
  organizationId: string;
  name?: string;
  averageRatingLast30Days?: number;
  unrespondedReviewCount?: number;
  subscriptionStatus?: string;
  [key: string]: unknown;
}

export interface AgencySwitchContextResponse {
  token: string;
  expiresAt?: string;
  targetOrganizationId?: string;
  [key: string]: unknown;
}

export interface InvoiceRecord {
  id: string;
  invoiceNumber?: string;
  organizationId?: string;
  amount?: number;
  currency?: string;
  status?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export interface SubscriptionPlan {
  id: string;
  name?: string;
  price?: number;
  annualPrice?: number;
  currency?: string;
  features?: string[];
  [key: string]: unknown;
}

export interface OrganizationMember {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  createdAt?: string;
  organizationId?: string;
  organizationName?: string;
  [key: string]: unknown;
}

export interface Review {
  id: string;
  rating?: number;
  platform?: string;
  title?: string;
  text?: string;
  content?: string;
  reviewText?: string;
  comment?: string;
  authorName?: string;
  author?: string;
  reviewerName?: string;
  createdAt?: string;
  date?: string;
  businessId?: string;
  platformReviewId?: string;
  syncedAt?: string;
  updatedAt?: string;
  sentiment?: string;
  response?: string;
  isResponded?: boolean;
  replyText?: string;
  replyUpdatedAt?: string;
  replySource?: 'revomira' | 'google' | string;
  analysis?: ReviewAnalysis;
  analysisResultId?: string;
  _id?: string;
  [key: string]: unknown;
}

export interface ReviewListParams {
  page?: number;
  pageSize?: number;
  platform?: string;
  sentiment?: string;
  minRating?: number;
  maxRating?: number;
}

export interface ReviewStatistics {
  totalReviews?: number;
  averageRating?: number;
  sentimentDistribution?: Record<string, number>;
}

export interface Subscription {
  id?: string;
  status?: string;
  organizationId?: string;
  trialEndDate?: string;
  trialEndsAt?: string;
  [key: string]: unknown;
}

export interface Plan {
  id: string;
  name?: string;
  price?: number;
  annualPrice?: number;
  currency?: string;
  billingCycle?: string;
  features?: string[];
  [key: string]: unknown;
}

export interface ChartPlatformItem {
  platform: string;
  reviews: number;
  percentage: number;
}

export interface ChartBusinessPerformance {
  name: string;
  rating: number;
  reviews: number;
}

export interface ChartTimeSeriesPoint {
  date: string;
  reviews: number;
  positive: number;
  negative: number;
}

export interface LoginApiResponse {
  accessToken: string;
  userId: string;
  name: string;
  email: string;
  role?: User['role'];
  organizationId: string;
  organizationName?: string;
  ManagedBusinessIds?: string[];
  managedBusinessIds?: string[];
  theme?: 'light' | 'dark';
  Theme?: 'light' | 'dark';
  language?: string;
  Language?: string;
}

export interface RegisterApiResponse {
  token?: string;
  Token?: string;
  userId?: string;
  UserId?: string;
  organizationId?: string;
  OrganizationId?: string;
  trialEndsAt?: string;
  TrialEndsAt?: string;
}

export function mapLoginResponse(response: LoginApiResponse): {
  token: string;
  user: User;
} {
  return {
    token: response.accessToken,
    user: {
      id: response.userId,
      name: response.name,
      email: response.email,
      role: response.role || 'OrganizationAdmin',
      organizationId: response.organizationId,
      organizationName: response.organizationName,
      managedBusinessIds: response.ManagedBusinessIds ?? response.managedBusinessIds ?? [],
      theme: response.theme ?? response.Theme,
      language: (response.language ?? response.Language) as User['language'],
    },
  };
}

export function mapRegisterResponse(response: RegisterApiResponse): {
  token: string | undefined;
  userId: string | undefined;
  organizationId: string | undefined;
} {
  return {
    token: response.token ?? response.Token,
    userId: response.userId ?? response.UserId,
    organizationId: response.organizationId ?? response.OrganizationId,
  };
}
